import type { FC, SVGProps } from "react"
import { useState } from "react"
import {
	IconCircleWarningOutline18 as AlertCircle,
	IconArrowRotateAnticlockwiseOutline18 as ArrowRotate,
	IconArrowUpRightOutline18 as ArrowUpRight,
	IconAtom2Outline18 as Atom,
	IconBookOpen4Outline18 as BookOpen,
	IconDatabaseOutline18 as Database,
	IconCodePullRequestOutline18 as GitPullRequest,
	IconMapOutline18 as MapIcon,
	IconStackPerspectiveOutline18 as StackPerspective,
	IconChatBubbleOutline18 as MessageSquare,
	IconPen2Outline18 as Pen,
	IconSquaresConnectedOutline18 as SquaresConnected,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { AiDraftSource } from "@productlane/lib/src/ai"
import { AI_BUDGET_REACHED_MESSAGE } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { Badge, Button, Popover, Tooltip } from "@productlane/ui"

export type AiDraftBadgeState =
	| "generating"
	| "available"
	| "applied"
	| "error"
	| "budget-reached"

interface Props {
	state: AiDraftBadgeState
	onApply?: () => void
	onDelete?: () => void
	onRegenerate?: () => void
	onAdjust?: (instruction: string) => void
	onOpenAiSettings?: () => void
	sources?: readonly AiDraftSource[] | null
}

const TYPE_META: Record<
	AiDraftSource["type"],
	{ icon: LucideIcon; label: string }
> = {
	docs: { icon: BookOpen, label: "Docs" },
	changelog: { icon: StackPerspective, label: "Changelog" },
	roadmap: { icon: MapIcon, label: "Roadmap" },
	thread: { icon: MessageSquare, label: "Thread" },
	github: { icon: GitPullRequest, label: "GitHub PR" },
	postgres: { icon: Database, label: "Query" },
	connector: { icon: SquaresConnected, label: "Connector" },
}

// Resolves a source to its destination URL. Internal Productlane routes are
// returned as relative paths so they open in a fresh app tab when set with
// `target="_blank"`. GitHub sources carry their own absolute URL.
function getSourceHref(source: AiDraftSource): string | null {
	if (source.type === "github") return source.url ?? null
	if (!source.id) return null
	switch (source.type) {
		case "thread":
			return `/threads/${source.id}`
		case "docs":
			return `/edit-docs/${source.id}`
		case "changelog":
			return `/edit-changelog/${source.id}`
		case "roadmap":
			return `/projects/${source.id}`
		default:
			return null
	}
}

// Tooltip body — a clean, list-bullet-free rundown of what the model
// consulted while drafting. Designed to feel quiet and precise: a single
// muted label, single-line truncated titles so long help-center article
// names don't wrap awkwardly, and a hover-only `ArrowUpRight` chevron that
// signals the row is clickable without shouting about it.
function SourcesList({ sources }: { sources: readonly AiDraftSource[] }) {
	return (
		<div className="flex w-64 flex-col p-1">
			<div className="px-1.5 pb-1.5 pt-0.5 text-[10.5px] font-medium tracking-[0.08em] text-gray-400 dark:text-gray-500">
				Sources
			</div>
			<div className="flex flex-col">
				{sources.map((s, i) => {
					const meta = TYPE_META[s.type]
					const Icon = meta?.icon ?? Atom
					const href = getSourceHref(s)
					const key = `${s.type}-${s.id ?? s.title}-${i}`

					const inner = (
						<>
							<Icon
								className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500"
								aria-hidden
							/>
							<span className="truncate text-[12.5px] text-gray-700 dark:text-gray-200">
								{s.title}
							</span>
							{s.type === "connector" && s.identifier ? (
								<span className="ml-auto shrink-0 font-mono text-[10.5px] text-gray-400 dark:text-gray-500">
									{s.identifier}
								</span>
							) : null}
						</>
					)

					if (href) {
						return (
							<a
								key={key}
								href={href}
								target="_blank"
								rel="noopener noreferrer"
								// stopPropagation so the click doesn't bubble to the
								// Tooltip content's `onClick={e => e.preventDefault()}`,
								// which would otherwise cancel navigation.
								onClick={(e) => e.stopPropagation()}
								className="group flex items-center gap-2 rounded-md px-1.5 py-1 outline-none transition-colors hover:bg-gray-100/80 focus-visible:bg-gray-100/80 dark:hover:bg-white/[0.04] dark:focus-visible:bg-white/[0.04]"
							>
								{inner}
								<ArrowUpRight
									className="ml-auto h-3 w-3 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 dark:text-gray-500"
									aria-hidden
								/>
							</a>
						)
					}

					// Non-routable (e.g. postgres query name) — plain label, no
					// hover affordance, no chevron. `cursor-default` so the row
					// doesn't pick up a text caret from any ancestor styling.
					return (
						<div
							key={key}
							className="flex cursor-default items-center gap-2 px-1.5 py-1"
						>
							{inner}
						</div>
					)
				})}
			</div>
		</div>
	)
}

function withSourcesTooltip(
	node: React.ReactNode,
	sources: readonly AiDraftSource[] | null | undefined,
) {
	if (!sources || sources.length === 0) return <>{node}</>
	return (
		<Tooltip
			label={<SourcesList sources={sources} />}
			side="top"
			// `!p-0` resets the tooltip's default content padding so our list
			// rows can extend edge-to-edge with their own hover backgrounds.
			contentClassName="!p-0"
		>
			{node}
		</Tooltip>
	)
}

export function AiDraftBadge({
	state,
	onApply,
	onDelete,
	onRegenerate,
	onAdjust,
	onOpenAiSettings,
	sources,
}: Props) {
	const [adjustOpen, setAdjustOpen] = useState(false)

	if (state === "generating") {
		// No tooltip while generating — sources aren't known yet. The shimmer
		// runs across the word only (Atom stays solid) to match the canonical
		// AI-loading affordance used in chat bubbles.
		return (
			<span
				// `cursor-default` so the parent header's `cursor-text` doesn't
				// leak through onto this non-interactive pill.
				className="inline-flex shrink-0 cursor-default items-center gap-1.5 whitespace-nowrap rounded-md bg-gray-100/80 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800/70 dark:text-gray-400"
				aria-live="polite"
			>
				<Atom className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
				<span className="ai-loading-shimmer">Drafting…</span>
			</span>
		)
	}

	// The workspace has spent its AI budget. This is a guard the user can lift
	// themselves, not a failure, so it stays in the neutral palette and carries
	// the fix: a link straight to the AI usage settings.
	if (state === "budget-reached") {
		return (
			<span
				className="inline-flex shrink-0 cursor-default items-center gap-1.5"
				aria-label="AI is paused, spend limit reached"
			>
				<Badge variant="default" className="px-1.5 text-xs">
					<AlertCircle className="h-3 w-3 flex-none opacity-50" aria-hidden />
					AI is paused
				</Badge>
				{/* The reason and the fix read without a hover: a tooltip is not a
				    place to explain why the agent went quiet. */}
				{onOpenAiSettings ? (
					<Button
						variant="link"
						size="xs"
						onMouseDown={(e) => e.preventDefault()}
						onClick={onOpenAiSettings}
					>
						Raise the spend limit
					</Button>
				) : (
					<Tooltip label={AI_BUDGET_REACHED_MESSAGE} side="top">
						<span className="text-xs text-gray-500 dark:text-gray-400">
							Spend limit reached
						</span>
					</Tooltip>
				)}
				{onRegenerate ? <RegenerateButton onRegenerate={onRegenerate} /> : null}
			</span>
		)
	}

	// "error" — quiet failure pill. Lives in the same visual weight class as
	// "applied" so it doesn't shout, but uses the muted red palette to read
	// as a problem at a glance. Hover reveals the full message via tooltip.
	if (state === "error") {
		return (
			<span
				className="inline-flex shrink-0 cursor-default items-center gap-1.5 whitespace-nowrap rounded-md bg-red-50 px-1.5 py-0.5 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300"
				aria-label="AI draft failed"
			>
				<Tooltip label="The AI draft couldn't be generated." side="top">
					<span className="inline-flex items-center gap-1">
						<AlertCircle className="h-3 w-3 flex-none opacity-50" aria-hidden />
						Draft failed
					</span>
				</Tooltip>
				{onRegenerate ? (
					<RegenerateButton onRegenerate={onRegenerate} tone="error" />
				) : null}
			</span>
		)
	}

	// A draft exists. The Atom icon becomes a trash on hover to delete it.
	// "available" re-applies on body click; "applied" body opens the
	// adjust popover, same as the pen.
	const canApply = state === "available" && !!onApply
	const canAdjust = !!onAdjust
	return (
		<span className="group/badge inline-flex shrink-0 cursor-default items-center gap-1 whitespace-nowrap rounded-md border border-green-500/30 bg-green-50 py-0.5 pl-1 pr-1.5 text-xs font-medium text-green-700 dark:border-green-400/30 dark:bg-green-500/10 dark:text-green-300">
			<Tooltip label="Delete AI draft" side="top">
				<button
					type="button"
					onMouseDown={(e) => e.preventDefault()}
					onClick={onDelete}
					aria-label="Delete AI draft"
					className="grid h-4 w-4 shrink-0 place-items-center rounded-sm text-green-700 outline-none transition-colors hover:text-green-900 focus-visible:ring-2 focus-visible:ring-green-500/40 dark:text-green-300 dark:hover:text-green-100 dark:focus-visible:ring-green-400/40"
				>
					<Atom
						className="ease-[cubic-bezier(0.23,1,0.32,1)] col-start-1 row-start-1 h-3 w-3 origin-center opacity-50 transition-[opacity,transform] duration-150 [transform-box:fill-box] group-hover/badge:scale-75 group-hover/badge:opacity-0"
						aria-hidden
					/>
					<Trash
						className="ease-[cubic-bezier(0.23,1,0.32,1)] col-start-1 row-start-1 h-3 w-3 origin-center scale-75 opacity-0 transition-[opacity,transform] duration-150 [transform-box:fill-box] group-hover/badge:scale-100 group-hover/badge:opacity-50"
						aria-hidden
					/>
				</button>
			</Tooltip>
			{withSourcesTooltip(
				canApply ? (
					<button
						type="button"
						onMouseDown={(e) => e.preventDefault()}
						onClick={onApply}
						aria-label="Use AI draft"
						className="shrink-0 rounded-sm outline-none transition-colors hover:text-green-900 focus-visible:ring-2 focus-visible:ring-green-500/40 dark:hover:text-green-200 dark:focus-visible:ring-green-400/40"
					>
						AI drafted
					</button>
				) : (
					<span className="shrink-0 cursor-default">AI drafted</span>
				),
				sources,
			)}
			{canAdjust ? (
				<AdjustDraftPopover
					open={adjustOpen}
					onOpenChange={setAdjustOpen}
					onSubmit={(instruction) => {
						setAdjustOpen(false)
						onAdjust(instruction)
					}}
				/>
			) : null}
		</span>
	)
}

// Prompt field for tweaking an existing draft. Enter submits and kicks off a
// regeneration that rewrites the current draft with the instruction applied.
function AdjustDraftPopover({
	open,
	onOpenChange,
	onSubmit,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (instruction: string) => void
}) {
	const [value, setValue] = useState("")
	const submit = () => {
		const instruction = value.trim()
		if (!instruction) return
		onSubmit(instruction)
		setValue("")
	}
	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			side="top"
			align="end"
			triggerTooltip="Adjust draft"
			className="w-80 !p-1"
			content={
				<form
					className="flex flex-col gap-1"
					onSubmit={(e) => {
						e.preventDefault()
						submit()
					}}
				>
					<textarea
						autoFocus
						rows={2}
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault()
								submit()
							}
						}}
						placeholder="Describe changes to the current draft…"
						aria-label="Adjust AI draft"
						className="w-full resize-none bg-transparent px-1.5 py-1 text-[12.5px] text-gray-800 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500"
					/>
					<div className="flex justify-end px-0.5 pb-0.5">
						<Button type="submit" size="xs" disabled={!value.trim()}>
							Adjust draft
						</Button>
					</div>
				</form>
			}
		>
			<button
				type="button"
				aria-label="Adjust AI draft"
				className="grid h-4 w-4 shrink-0 place-items-center rounded-sm text-green-700 outline-none transition-colors hover:text-green-900 focus-visible:ring-2 focus-visible:ring-green-500/40 dark:text-green-300 dark:hover:text-green-100 dark:focus-visible:ring-green-400/40"
			>
				<Pen className="h-3.5 w-3.5 flex-none opacity-50" aria-hidden />
			</button>
		</Popover>
	)
}

function RegenerateButton({
	onRegenerate,
	tone = "neutral",
}: {
	onRegenerate: () => void
	tone?: "neutral" | "error"
}) {
	return (
		<Tooltip label="Regenerate draft" side="top">
			<button
				type="button"
				onMouseDown={(e) => e.preventDefault()}
				onClick={onRegenerate}
				aria-label="Regenerate AI draft"
				className={cn(
					"grid h-4 w-4 flex-none place-items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
					tone === "error"
						? "text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"
						: "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100",
				)}
			>
				<ArrowRotate className="h-3 w-3 flex-none" aria-hidden />
			</button>
		</Tooltip>
	)
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
